/**
 * In-App Purchase API
 *
 * Posts a validated store receipt to the main-server, which validates it with
 * Apple/Google, credits the wallet (server is the source of truth for delivery),
 * and returns the granted contents + new balance. Mirrors avatarApi's auth shape.
 *
 * The CLIENT never grants currency — it sends the receipt and renders what the
 * server delivered (server-authoritative, per CLAUDE.md).
 */

import { serverConfig } from '../network/ServerConfig';

export interface IapGranted {
  stacks: number;
  gold: number;
  diceTokens: number;
  /** Direct dice unlocks (offers only; static packs grant currency). */
  dice?: string[];
}

export interface IapBalance {
  stacks: number;
  gold: number;
  diceTokens: number;
}

export interface PurchaseRequest {
  productId: string;
  platform: 'ios' | 'android';
  receipt?: string;         // iOS: base64 app receipt
  purchaseToken?: string;   // Android: Play Billing purchase token
  surface?: string;         // analytics (spec §8e): store_browse | startup_popup | ...
  offerId?: string;         // set when bought through an offer SKU
}

export interface PurchaseResult {
  ok: boolean;
  duplicate?: boolean;
  granted?: IapGranted;
  balance: IapBalance;
  error?: string;
}

const EMPTY_BALANCE: IapBalance = { stacks: 0, gold: 0, diceTokens: 0 };

/** Send a receipt to the server for validation + grant. Never throws — returns
 *  an `{ ok: false, error }` result so the caller can finish() the transaction
 *  and surface a toast without an unhandled rejection. */
export async function purchaseBundle(req: PurchaseRequest, token: string): Promise<PurchaseResult> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/store/purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(req),
    });
    const body = await resp.json().catch(() => ({} as Record<string, unknown>));
    if (!resp.ok || !body?.ok) {
      return {
        ok: false,
        balance: (body?.balance as IapBalance) ?? EMPTY_BALANCE,
        error: (body?.error as string) ?? `purchase_failed_${resp.status}`,
      };
    }
    return body as PurchaseResult;
  } catch (err) {
    return { ok: false, balance: EMPTY_BALANCE, error: 'network_error' };
  }
}
